import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subQuarters, subYears, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { useDateRangeFilter } from "@/hooks/useDateRangeFilter";
import { useDateRangeFallbackToast } from "@/hooks/useDateRangeFallbackToast";
import { DateRangeFilter } from "@/components/common/DateRangeFilter";
import { ExportRangeSummary } from "@/components/common/ExportRangeSummary";
import { datePresets } from "@/pages/workshop/utils/dateRange";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  ShoppingCart,
  Download,
  Loader2,
  Eye,
  Package,
  X,
  AlertTriangle,
  FileText,
  Upload,
  Sparkles,
  
  Scale,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { InlineDateEdit } from "@/components/ui/inline-date-edit";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoiceImportDialog } from "@/components/erp/InvoiceImportDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { useTableSort } from "@/hooks/useTableSort";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SortResetButton } from "@/components/ui/sort-reset-button";

interface Order {
  id: string;
  order_number: string;
  client_id: string | null;
  supplier_id: string | null;
  warehouse_id: string | null;
  order_type: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  currency: string;
  notes: string | null;
  order_date: string;
  expected_date: string | null;
  created_at: string;
  invoice_file_path: string | null;
  payment_method: string | null;
  client?: {
    id: string;
    name: string;
    company_name: string | null;
  };
  supplier?: {
    id: string;
    name: string;
    logo_url?: string | null;
  };
  warehouse?: {
    id: string;
    name: string;
  };
}

interface Client {
  id: string;
  name: string;
  company_name: string | null;
}

interface Supplier {
  id: string;
  name: string;
  contact_name: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  ean: string | null;
  unit_price: number | null;
  currency: string | null;
  stock: number;
}

interface OrderItem {
  product_id: string;
  product_name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  total: number;
}

interface WarehouseOption {
  id: string;
  name: string;
}

const statusConfig: Record<string, { label: string; class: string }> = {
  pending: { label: "Pendiente", class: "badge-status-warning" },
  confirmed: { label: "Confirmado", class: "badge-status-info" },
  processing: { label: "En Proceso", class: "badge-status-info" },
  shipped: { label: "Enviado", class: "badge-status-success" },
  delivered: { label: "Entregado", class: "badge-status-success" },
  cancelled: { label: "Cancelado", class: "badge-status-danger" },
};

const orderTypes = [
  { value: "sale", label: "Venta" },
  { value: "purchase", label: "Compra" },
  { value: "quote", label: "Cotización" },
];

const paymentMethods = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "bizum", label: "Bizum" },
  { value: "paypal", label: "PayPal" },
  { value: "domiciliation", label: "Domiciliación" },
  { value: "check", label: "Cheque" },
  { value: "other", label: "Otro" },
];

const defaultTaxTypes = [
  { value: "iva", label: "IVA 21%", rate: 0.21 },
  { value: "iva_req", label: "IVA+Req 26,2%", rate: 0.262 },
];

const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const getExportPeriodDates = (
  mode: "monthly" | "quarterly" | "yearly",
  year: number,
  month: number,
  quarter: number
): { from: string; to: string } => {
  switch (mode) {
    case "monthly": {
      const from = new Date(year, month, 1);
      const to = new Date(year, month + 1, 0);
      return { from: from.toISOString().split("T")[0], to: to.toISOString().split("T")[0] };
    }
    case "quarterly": {
      const qStart = (quarter - 1) * 3;
      const from = new Date(year, qStart, 1);
      const to = new Date(year, qStart + 3, 0);
      return { from: from.toISOString().split("T")[0], to: to.toISOString().split("T")[0] };
    }
    case "yearly":
      return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
};

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Filtro de fechas unificado. "all" (todos los periodos) se representa como preset=null.
  const {
    preset: datePreset,
    customFrom,
    customTo,
    activeRange,
    setFilter: setDateFilter,
    fallbackReason,
  } = useDateRangeFilter({ defaultPreset: null });
  useDateRangeFallbackToast(fallbackReason);
  const dateFrom: Date | null = activeRange?.from ?? null;
  const dateTo: Date | null = activeRange?.to ?? null;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState<Order | null>(null);
  const [viewOrderItems, setViewOrderItems] = useState<OrderItem[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [attachingFile, setAttachingFile] = useState(false);
  const [taxTypes, setTaxTypes] = useState(defaultTaxTypes);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditFields, setBulkEditFields] = useState<Record<string, any>>({});

  const [formData, setFormData] = useState({
    client_id: "",
    supplier_id: "",
    warehouse_id: "",
    order_type: "purchase",
    status: "pending",
    payment_method: "",
    subtotal: 0,
    tax_type: "iva",
    tax_amount: 0,
    total: 0,
    currency: "EUR",
    notes: "",
    order_date: new Date().toISOString().split("T")[0],
    expected_date: "",
    supplier_invoice_number: "",
    global_discount_percent: 0,
    missing_equivalence_surcharge: false,
    missing_surcharge_rate: 0,
    missing_surcharge_amount: 0,
    include_in_model_309: false,
    iva_309_expense: false,
    iva_309_mode: "" as "" | "iva_re" | "solo_iva",
    iva_309_expense_rate: 0,
    iva_309_expense_amount: 0,
    iva_309_re_amount: 0,
  });
  const { user } = useAuth();
  const { canAccess } = usePermissions();
  const { toast } = useToast();

  useEffect(() => {
    fetchOrders();
    fetchClients();
    fetchSuppliers();
    fetchProducts();
    fetchWarehouses();
    fetchTaxTypes();
  }, []);

  const fetchTaxTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("supplier_tax_types")
        .select("name, value, rate")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      if (data && data.length > 0) {
        setTaxTypes((data as any[]).map(t => ({ value: t.value, label: t.name, rate: Number(t.rate) })));
      }
    } catch (e) {
      console.error("Error fetching tax types:", e);
    }
  };


  const fetchWarehouses = async () => {
    try {
      const { data, error } = await supabase
        .from("warehouses")
        .select("id, name")
        .eq("status", "active")
        .order("name");

      if (error) throw error;
      setWarehouses(data || []);
    } catch (error: any) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, company_name")
        .eq("status", "active");

      if (error) throw error;
      setClients(data || []);
    } catch (error: any) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, name, contact_name, default_tax_type")
        .eq("status", "active");

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error: any) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch products with their inventory stock
      const { data: productsData, error: productsError } = await supabase
        .from("products")
        .select("id, name, sku, ean, unit_price, currency")
        .eq("status", "active")
        .neq("product_type", "expense");

      if (productsError) throw productsError;

      // Fetch inventory to get stock levels
      const { data: inventoryData, error: inventoryError } = await supabase
        .from("inventory")
        .select("product_id, quantity, reserved_quantity");

      if (inventoryError) throw inventoryError;

      // Map products with their stock
      const productsWithStock = (productsData || []).map((product) => {
        const inventoryItem = inventoryData?.find((inv) => inv.product_id === product.id);
        const stock = inventoryItem 
          ? (inventoryItem.quantity || 0) - (inventoryItem.reserved_quantity || 0)
          : 0;
        return { 
          ...product, 
          unit_price: product.unit_price ?? 0,
          stock 
        };
      });

      setProducts(productsWithStock);
    } catch (error: any) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          client:clients(id, name, company_name),
          supplier:suppliers(id, name, logo_url),
          warehouse:warehouses(id, name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las órdenes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateOrderNumber = () => {
    const prefix = "ORD";
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, "");
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}-${date}-${random}`;
  };

  const openNewDialog = () => {
    setEditingOrder(null);
    setOrderItems([]);
    setInvoiceFile(null);
    setFormData({
      client_id: "",
      supplier_id: "",
      warehouse_id: warehouses.length > 0 ? warehouses[0].id : "",
      order_type: "purchase",
      status: "pending",
      payment_method: "",
      subtotal: 0,
      tax_type: "iva",
      tax_amount: 0,
      total: 0,
      currency: "EUR",
      notes: "",
      order_date: new Date().toISOString().split("T")[0],
      expected_date: "",
      supplier_invoice_number: "",
      global_discount_percent: 0,
      missing_equivalence_surcharge: false,
      missing_surcharge_rate: 0,
      missing_surcharge_amount: 0,
      include_in_model_309: false,
      iva_309_expense: false,
      iva_309_mode: "" as "" | "iva_re" | "solo_iva",
      iva_309_expense_rate: 0,
      iva_309_expense_amount: 0,
      iva_309_re_amount: 0,
    });
    setDialogOpen(true);
  };

  const openEditDialog = async (order: Order) => {
    setEditingOrder(order);
    setInvoiceFile(null);
    // Determine tax type from stored tax_type or infer from amounts
    const storedTaxType = (order as any).tax_type;
    const taxType = storedTaxType === 'iva_req' ? 'iva_req' : 
                    storedTaxType === 'iva' ? 'iva' :
                    // Legacy fallback: infer from tax rate
                    (order.subtotal > 0 && Math.abs((order.tax_amount / order.subtotal) - 0.262) < 0.01) ? 'iva_req' : 'iva';
    
    // Load order items
    try {
      const { data: orderItemsData, error } = await supabase
        .from("order_items")
        .select(`
          product_id,
          quantity,
          unit_price,
          total,
          product:products(name, sku)
        `)
        .eq("order_id", order.id);

      if (error) throw error;

      const loadedItems: OrderItem[] = (orderItemsData || []).map((item: any) => ({
        product_id: item.product_id,
        product_name: item.product?.name || "Producto eliminado",
        sku: item.product?.sku || "",
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_percent: item.discount || 0,
        total: item.total,
      }));

      setOrderItems(loadedItems);
    } catch (error: any) {
      console.error("Error loading order items:", error);
      setOrderItems([]);
    }

    setFormData({
      client_id: order.client_id || "",
      supplier_id: (order as any).supplier_id || "",
      warehouse_id: order.warehouse_id || "",
      order_type: order.order_type,
      status: order.status,
      payment_method: order.payment_method || "",
      subtotal: order.subtotal,
      tax_type: taxType,
      tax_amount: order.tax_amount,
      total: order.total,
      currency: order.currency,
      notes: order.notes || "",
      order_date: order.order_date,
      expected_date: order.expected_date || "",
      supplier_invoice_number: (order as any).supplier_invoice_number || "",
      global_discount_percent: (order as any).global_discount_percent || 0,
      missing_equivalence_surcharge: (order as any).missing_equivalence_surcharge || false,
      missing_surcharge_rate: (order as any).missing_surcharge_rate || 0,
      missing_surcharge_amount: (order as any).missing_surcharge_amount || 0,
      include_in_model_309: (order as any).include_in_model_309 || false,
      iva_309_expense: (order as any).iva_309_expense || false,
      iva_309_mode: (order as any).iva_309_mode || "",
      iva_309_expense_rate: (order as any).iva_309_expense_rate || 0,
      iva_309_expense_amount: (order as any).iva_309_expense_amount || 0,
      iva_309_re_amount: (order as any).iva_309_re_amount || 0,
    });
    setDialogOpen(true);
  };

  const recalculateTotals = (items: OrderItem[], taxType: string, globalDiscPct?: number) => {
    const rawSubtotal = items.reduce((acc, item) => acc + item.total, 0);
    const discPct = globalDiscPct !== undefined ? globalDiscPct : formData.global_discount_percent;
    const subtotal = rawSubtotal * (1 - discPct / 100);
    const taxRate = taxTypes.find(t => t.value === taxType)?.rate || 0.21;
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal: rawSubtotal,
      tax_amount: taxAmount,
      total,
      global_discount_percent: discPct,
    }));
  };

  const handleTaxTypeChange = (taxType: string) => {
    const taxRate = taxTypes.find(t => t.value === taxType)?.rate || 0.21;
    const taxAmount = formData.subtotal * taxRate;
    setFormData({
      ...formData,
      tax_type: taxType,
      tax_amount: taxAmount,
      total: formData.subtotal + taxAmount,
    });
  };

  const addProductToOrder = (product: Product) => {
    const existingItem = orderItems.find(item => item.product_id === product.id);
    
    if (existingItem) {
      const updatedItems = orderItems.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price * (1 - item.discount_percent / 100) }
          : item
      );
      setOrderItems(updatedItems);
      recalculateTotals(updatedItems, formData.tax_type);
    } else {
      const newItem: OrderItem = {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        quantity: 1,
        unit_price: product.unit_price,
        discount_percent: 0,
        total: product.unit_price,
      };
      const updatedItems = [...orderItems, newItem];
      setOrderItems(updatedItems);
      recalculateTotals(updatedItems, formData.tax_type);
    }
    setProductSearchOpen(false);
  };

  const updateItemQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItemFromOrder(productId);
      return;
    }
    
    const updatedItems = orderItems.map(item =>
      item.product_id === productId
        ? { ...item, quantity, total: quantity * item.unit_price * (1 - item.discount_percent / 100) }
        : item
    );
    setOrderItems(updatedItems);
    recalculateTotals(updatedItems, formData.tax_type);
  };

  const updateItemDiscount = (productId: string, discountPercent: number) => {
    const updatedItems = orderItems.map(item =>
      item.product_id === productId
        ? { ...item, discount_percent: discountPercent, total: item.quantity * item.unit_price * (1 - discountPercent / 100) }
        : item
    );
    setOrderItems(updatedItems);
    recalculateTotals(updatedItems, formData.tax_type);
  };

  const removeItemFromOrder = (productId: string) => {
    const updatedItems = orderItems.filter(item => item.product_id !== productId);
    setOrderItems(updatedItems);
    recalculateTotals(updatedItems, formData.tax_type);
  };

  const handleSave = async () => {
    // Validate at least one product
    if (orderItems.length === 0) {
      toast({
        title: "Error",
        description: "Debes añadir al menos un producto a la orden",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      let orderId: string;
      let invoiceFilePath: string | null = editingOrder?.invoice_file_path || null;

      // Upload invoice file if provided
      if (invoiceFile) {
        setUploadingInvoice(true);
        const uploadFormData = new FormData();
        uploadFormData.append("file", invoiceFile);
        const { data: uploadResult, error: uploadError } = await supabase.functions.invoke("upload-order-invoice", {
          body: uploadFormData,
        });
        if (uploadError) throw uploadError;
        if (uploadResult?.error) throw new Error(uploadResult.error);
        invoiceFilePath = uploadResult?.path;
        setUploadingInvoice(false);

        // Also upload to FTP server if configured
        try {
          const ftpFormData = new FormData();
          ftpFormData.append("file", invoiceFile);
          ftpFormData.append("fileName", `factura-${editingOrder?.order_number || generateOrderNumber()}.pdf`);
          ftpFormData.append("orderDate", formData.order_date);

          await supabase.functions.invoke("upload-order-invoice-ftp", {
            body: ftpFormData,
          });
          // Don't fail the whole save if FTP fails - it's secondary
        } catch (ftpError) {
          console.warn("FTP upload failed (non-critical):", ftpError);
        }
      }

      if (editingOrder) {
        // Update order
        const { error } = await supabase
          .from("orders")
          .update({
            client_id: formData.client_id || null,
            supplier_id: formData.supplier_id || null,
            warehouse_id: formData.warehouse_id || null,
            order_type: formData.order_type,
            status: formData.status,
            payment_method: formData.payment_method || null,
            subtotal: formData.subtotal,
            tax_amount: formData.tax_amount,
            total: formData.total,
            currency: formData.currency,
            notes: formData.notes || null,
            order_date: formData.order_date,
            expected_date: formData.expected_date || null,
            invoice_file_path: invoiceFilePath,
            supplier_invoice_number: formData.supplier_invoice_number || null,
            tax_type: formData.tax_type,
            missing_equivalence_surcharge: formData.missing_equivalence_surcharge,
            missing_surcharge_rate: formData.missing_surcharge_rate,
            missing_surcharge_amount: formData.missing_surcharge_amount,
            include_in_model_309: formData.include_in_model_309 || formData.iva_309_expense,
            iva_309_expense: formData.iva_309_expense,
            iva_309_mode: formData.iva_309_mode || null,
            iva_309_expense_rate: formData.iva_309_expense_rate,
            iva_309_expense_amount: formData.iva_309_expense_amount,
            iva_309_re_amount: formData.iva_309_re_amount,
          } as any)
          .eq("id", editingOrder.id);

        if (error) throw error;
        orderId = editingOrder.id;

        // Delete existing order items
        const { error: deleteError } = await supabase
          .from("order_items")
          .delete()
          .eq("order_id", orderId);

        if (deleteError) throw deleteError;
      } else {
        // Create new order
        const { data: newOrder, error } = await supabase
          .from("orders")
          .insert({
            order_number: generateOrderNumber(),
            client_id: formData.client_id || null,
            supplier_id: formData.supplier_id || null,
            warehouse_id: formData.warehouse_id || null,
            order_type: formData.order_type,
            status: formData.status,
            payment_method: formData.payment_method || null,
            subtotal: formData.subtotal,
            tax_amount: formData.tax_amount,
            total: formData.total,
            currency: formData.currency,
            notes: formData.notes || null,
            order_date: formData.order_date,
            expected_date: formData.expected_date || null,
            created_by: user?.id,
            invoice_file_path: invoiceFilePath,
            supplier_invoice_number: formData.supplier_invoice_number || null,
            tax_type: formData.tax_type,
            missing_equivalence_surcharge: formData.missing_equivalence_surcharge,
            missing_surcharge_rate: formData.missing_surcharge_rate,
            missing_surcharge_amount: formData.missing_surcharge_amount,
            include_in_model_309: formData.include_in_model_309 || formData.iva_309_expense,
            iva_309_expense: formData.iva_309_expense,
            iva_309_mode: formData.iva_309_mode || null,
            iva_309_expense_rate: formData.iva_309_expense_rate,
            iva_309_expense_amount: formData.iva_309_expense_amount,
            iva_309_re_amount: formData.iva_309_re_amount,
          } as any)
          .select("id")
          .single();

        if (error) throw error;
        orderId = newOrder.id;
      }

      // Insert order items
      const orderItemsToInsert = orderItems.map((item) => ({
        order_id: orderId,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
        discount: 0,
      }));

      const { error: itemsError } = await supabase
        .from("order_items")
        .insert(orderItemsToInsert);

      if (itemsError) throw itemsError;

      toast({ title: editingOrder ? "Orden actualizada" : "Orden creada" });
      setDialogOpen(false);
      fetchOrders();
    } catch (error: any) {
      console.error("Error saving order:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la orden",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setUploadingInvoice(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de eliminar esta orden? Se restará el stock de los productos asociados.")) return;

    try {
      // 1. Get order items before deleting
      const { data: items, error: itemsError } = await supabase
        .from("order_items")
        .select("product_id, quantity")
        .eq("order_id", id);

      if (itemsError) throw itemsError;

      // 2. Subtract stock from inventory for each product
      if (items && items.length > 0) {
        for (const item of items) {
          const { data: inv } = await supabase
            .from("inventory")
            .select("id, quantity")
            .eq("product_id", item.product_id)
            .maybeSingle();

          if (inv) {
            await supabase
              .from("inventory")
              .update({ 
                quantity: Math.max(0, (inv.quantity || 0) - item.quantity),
                last_movement_date: new Date().toISOString(),
              })
              .eq("id", inv.id);
          }
        }
      }

      // 3. Delete the order (order_items should cascade)
      const { error } = await supabase.from("orders").delete().eq("id", id);
      if (error) throw error;

      toast({ title: "Orden eliminada y stock actualizado" });
      fetchOrders();
      fetchProducts(); // Refresh product stock
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // handleDatePreset / clearDateFilter eliminados: gestionados por DateRangeFilter + useDateRangeFilter.
  // "Quitar filtro" en DateRangeFilter llama setFilter({preset: null}) = equivalente a "all".


  // Bulk status change
  const handleBulkStatusChange = async (status: string) => {
    try {
      const ids = Array.from(selectedOrders);
      const { error } = await supabase.from("orders").update({ status } as any).in("id", ids);
      if (error) throw error;
      toast({ title: `${ids.length} órdenes actualizadas a "${statusConfig[status]?.label || status}"` });
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedOrders);
    if (!confirm(`¿Eliminar ${ids.length} órdenes seleccionadas?`)) return;
    try {
      const { error } = await supabase.from("orders").delete().in("id", ids);
      if (error) throw error;
      toast({ title: `${ids.length} órdenes eliminadas` });
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Bulk edit fields
  const handleBulkEdit = async () => {
    const ids = Array.from(selectedOrders);
    const payload: any = {};
    if (bulkEditFields.status) payload.status = bulkEditFields.status;
    if (bulkEditFields.payment_method) payload.payment_method = bulkEditFields.payment_method;
    if (bulkEditFields.order_type) payload.order_type = bulkEditFields.order_type;
    if (bulkEditFields.supplier_id) payload.supplier_id = bulkEditFields.supplier_id;
    if (bulkEditFields.warehouse_id) payload.warehouse_id = bulkEditFields.warehouse_id;
    if (bulkEditFields.tax_type) payload.tax_type = bulkEditFields.tax_type;
    if (bulkEditFields.notes !== undefined && bulkEditFields.notes !== "") payload.notes = bulkEditFields.notes;
    if (bulkEditFields.missing_equivalence_surcharge !== undefined) payload.missing_equivalence_surcharge = bulkEditFields.missing_equivalence_surcharge;
    if (bulkEditFields.iva_309_expense !== undefined) payload.iva_309_expense = bulkEditFields.iva_309_expense;
    if (bulkEditFields.iva_309_mode) payload.iva_309_mode = bulkEditFields.iva_309_mode;

    if (Object.keys(payload).length === 0) {
      toast({ title: "Sin cambios", description: "No hay campos seleccionados", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase.from("orders").update(payload).in("id", ids);
      if (error) throw error;
      toast({ title: `${ids.length} órdenes actualizadas` });
      setSelectedOrders(new Set());
      setBulkEditOpen(false);
      setBulkEditFields({});
      fetchOrders();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const toggleSelectAll = () => {
    if (sortedData.length === 0) return;
    if (selectedOrders.size === sortedData.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(sortedData.map(o => o.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredOrders = orders.filter((order) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      order.order_number.toLowerCase().includes(search) ||
      (order.client?.name?.toLowerCase()?.includes(search) ?? false) ||
      (order.supplier?.name?.toLowerCase()?.includes(search) ?? false);
    
    let matchesStatus: boolean;
    if (statusFilter === "all") {
      matchesStatus = true;
    } else if (statusFilter === "pending_309") {
      matchesStatus = (order as any).missing_equivalence_surcharge === true;
    } else {
      matchesStatus = order.status === statusFilter;
    }
    
    // Date filters
    const orderDate = new Date(order.order_date);
    const matchesDateFrom = !dateFrom || orderDate >= dateFrom;
    const matchesDateTo = !dateTo || orderDate <= dateTo;
    
    return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const { sortState, handleSort, clearSort, sortedData, isSorted } = useTableSort(filteredOrders);



  const openViewDialog = async (order: Order) => {
    setViewOrder(order);
    setViewDialogOpen(true);
    try {
      const { data, error } = await supabase
        .from("order_items")
        .select(`product_id, quantity, unit_price, total, product:products(name, sku)`)
        .eq("order_id", order.id);
      if (error) throw error;
      setViewOrderItems((data || []).map((item: any) => ({
        product_id: item.product_id,
        product_name: item.product?.name || "Producto eliminado",
        sku: item.product?.sku || "",
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_percent: item.discount || 0,
        total: item.total,
      })));
    } catch {
      setViewOrderItems([]);
    }
  };

  const handleAttachFile = async (order: Order, file: File) => {
    setAttachingFile(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("order_id", order.id);
      const { data: uploadResult, error: uploadError } = await supabase.functions.invoke("upload-order-invoice", {
        body: uploadFormData,
      });
      if (uploadError) throw uploadError;
      if (uploadResult?.error) throw new Error(uploadResult.error);
      const filePath = uploadResult?.path;
      toast({ title: "Archivo adjuntado correctamente" });
      setViewOrder({ ...order, invoice_file_path: filePath });
      fetchOrders();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "No se pudo adjuntar el archivo", variant: "destructive" });
    } finally {
      setAttachingFile(false);
    }
  };

  const handleDownloadInvoice = async (order: Order) => {
    if (!order.invoice_file_path) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('order-invoices')
        .download(order.invoice_file_path);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const ext = order.invoice_file_path.split('.').pop() || 'pdf';
      a.download = `factura-${order.order_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo descargar la factura",
        variant: "destructive",
      });
    }
  };

  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMode, setExportMode] = useState<"monthly" | "quarterly" | "yearly">("monthly");
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth());
  const [exportQuarter, setExportQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [exportStatus, setExportStatus] = useState("all");
  const [exportTaxType, setExportTaxType] = useState("all");

  const exportFilteredOrders = useMemo(() => {
    const { from, to } = getExportPeriodDates(exportMode, exportYear, exportMonth, exportQuarter);
    return orders.filter((o) => {
      const d = new Date(o.order_date);
      const inRange = d >= new Date(from) && d <= new Date(to);
      const matchesStatus = exportStatus === "all" || o.status === exportStatus;
      const matchesTaxType = exportTaxType === "all" || (o as any).tax_type === exportTaxType;
      return inRange && matchesStatus && matchesTaxType;
    });
  }, [orders, exportMode, exportYear, exportMonth, exportQuarter, exportStatus, exportTaxType]);

  const exportPeriodLabel = useMemo(() => {
    if (exportMode === "monthly") return `${monthNames[exportMonth]}_${exportYear}`;
    if (exportMode === "quarterly") return `T${exportQuarter}_${exportYear}`;
    return `${exportYear}`;
  }, [exportMode, exportYear, exportMonth, exportQuarter]);

  const handleExportExcel = async () => {
    if (exportFilteredOrders.length === 0) {
      toast({ title: "Sin datos", description: "No hay órdenes en el período seleccionado", variant: "destructive" });
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Órdenes");
      sheet.columns = [
        { header: "Nº Orden", key: "order_number", width: 20 },
        { header: "Nº Fact. Proveedor", key: "supplier_invoice_number", width: 20 },
        { header: "Proveedor", key: "supplier", width: 25 },
        { header: "Tipo", key: "type", width: 12 },
        { header: "Modo de Pago", key: "payment_method", width: 16 },
        { header: "Subtotal", key: "subtotal", width: 14 },
        { header: "Impuestos", key: "tax", width: 14 },
        { header: "Total", key: "total", width: 14 },
        { header: "Moneda", key: "currency", width: 8 },
        { header: "Fecha", key: "date", width: 14 },
        { header: "Estado", key: "status", width: 14 },
        { header: "Notas", key: "notes", width: 30 },
      ];
      sheet.getRow(1).font = { bold: true };
      exportFilteredOrders.forEach((order) => {
        sheet.addRow({
          order_number: order.order_number,
          supplier_invoice_number: (order as any).supplier_invoice_number || "",
          supplier: order.client?.name || "",
          type: orderTypes.find(t => t.value === order.order_type)?.label || order.order_type,
          payment_method: paymentMethods.find(p => p.value === order.payment_method)?.label || order.payment_method || "",
          subtotal: order.subtotal,
          tax: order.tax_amount,
          total: order.total,
          currency: order.currency,
          date: order.order_date,
          status: statusConfig[order.status]?.label || order.status,
          notes: order.notes || "",
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ordenes_${exportPeriodLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Exportación completada", description: `${exportFilteredOrders.length} registros exportados` });
      setExportDialogOpen(false);
    } catch (error: any) {
      toast({ title: "Error", description: "No se pudo exportar", variant: "destructive" });
    }
  };

  const MAX_ZIP_SIZE = 5 * 1024 * 1024; // 5MB

  const getZipBaseName = () => {
    const taxSuffix = exportTaxType === "all" ? "" : exportTaxType === "iva_req" ? "-IVA+Req" : "-IVA";
    if (exportMode === "monthly") {
      const mesCapitalized = monthNames[exportMonth].charAt(0).toUpperCase() + monthNames[exportMonth].slice(1);
      return `Gastos-${mesCapitalized}-${exportYear}${taxSuffix}`;
    }
    if (exportMode === "quarterly") {
      return `Gastos-${exportQuarter}Trimestre-${exportYear}${taxSuffix}`;
    }
    return `Gastos-${exportYear}${taxSuffix}`;
  };

  const handleExportZip = async () => {
    const withInvoices = exportFilteredOrders.filter(o => o.invoice_file_path);
    if (withInvoices.length === 0) {
      toast({ title: "Sin facturas", description: "No hay facturas PDF/imágenes en el período seleccionado", variant: "destructive" });
      return;
    }
    setDownloadingZip(true);
    setDownloadProgress(0);
    try {
      // Download all files first
      const files: { name: string; blob: Blob }[] = [];
      let done = 0;
      for (const order of withInvoices) {
        if (!order.invoice_file_path) { done++; continue; }
        const { data, error } = await supabase.storage.from('order-invoices').download(order.invoice_file_path);
        done++;
        setDownloadProgress(Math.round((done / withInvoices.length) * 80));
        if (error) { console.error(`Error downloading ${order.order_number}:`, error); continue; }
        const ext = order.invoice_file_path.split('.').pop() || 'pdf';
        files.push({ name: `factura-${order.order_number}-${order.order_date}.${ext}`, blob: data });
      }

      if (files.length === 0) {
        toast({ title: "Error", description: "No se pudo descargar ningún archivo", variant: "destructive" });
        return;
      }

      // Split into ZIP parts of max 5MB
      const baseName = getZipBaseName();
      const zipParts: Blob[] = [];
      let currentZip = new JSZip();
      let currentSize = 0;

      for (const file of files) {
        const fileSize = file.blob.size;
        // If adding this file would exceed limit and zip already has files, finalize current zip
        if (currentSize + fileSize > MAX_ZIP_SIZE && currentSize > 0) {
          zipParts.push(await currentZip.generateAsync({ type: "blob" }));
          currentZip = new JSZip();
          currentSize = 0;
        }
        currentZip.file(file.name, file.blob);
        currentSize += fileSize;
      }
      // Add remaining files
      if (currentSize > 0) {
        zipParts.push(await currentZip.generateAsync({ type: "blob" }));
      }

      setDownloadProgress(95);

      // Download each part
      for (let i = 0; i < zipParts.length; i++) {
        const fileName = zipParts.length === 1
          ? `${baseName}.zip`
          : `${baseName}-Parte ${i + 1}.zip`;
        const url = URL.createObjectURL(zipParts[i]);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        // Small delay between downloads
        if (i < zipParts.length - 1) await new Promise(r => setTimeout(r, 500));
      }

      setDownloadProgress(100);
      const partsMsg = zipParts.length > 1 ? ` en ${zipParts.length} partes` : "";
      toast({ title: "Descarga completada", description: `${files.length} archivo(s)${partsMsg}` });
      setExportDialogOpen(false);
    } catch (error: any) {
      toast({ title: "Error", description: "No se pudo crear el ZIP", variant: "destructive" });
    } finally {
      setDownloadingZip(false);
      setDownloadProgress(0);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const totalExpenses = filteredOrders
    .filter((o) => o.order_type === "purchase" && o.status !== "cancelled")
    .reduce((acc, o) => acc + (o.total ?? 0), 0);

  const cancelledOrders = filteredOrders.filter((o) => o.status === "cancelled").length;

  const purchaseOrders = filteredOrders.filter((o) => o.order_type === "purchase" && o.status !== "cancelled");

  const totalIVA = purchaseOrders.reduce((acc, o) => acc + (Number((o as any).tax_amount) || 0), 0);

  const totalREPagado = purchaseOrders
    .filter((o) => (o as any).tax_type === "iva_req")
    .reduce((acc, o) => {
      const subtotal = Number((o as any).subtotal) || 0;
      return acc + subtotal * 0.052;
    }, 0);

  const totalREPendiente = purchaseOrders
    .filter((o) => (o as any).missing_equivalence_surcharge)
    .reduce((acc, o) => acc + (Number((o as any).missing_surcharge_amount) || 0), 0);

  // Suppliers exceeding €3,000
  const supplierTotals = filteredOrders
    .filter((o) => o.order_type === "purchase" && o.status !== "cancelled" && o.supplier_id)
    .reduce<Record<string, { name: string; total: number }>>((acc, o) => {
      const sId = o.supplier_id!;
      const sName = o.supplier?.name || "Desconocido";
      if (!acc[sId]) acc[sId] = { name: sName, total: 0 };
      acc[sId].total += o.total ?? 0;
      return acc;
    }, {});
  const suppliersOver3k = Object.values(supplierTotals).filter((s) => s.total >= 3000);

  const canCreate = canAccess("erp_orders", "create");
  const canEdit = canAccess("erp_orders", "edit");
  const canDelete = canAccess("erp_orders", "delete");

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Órdenes" description="Gestiona órdenes de compra y proveedores">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/alquiler">Gestión de alquileres</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/clientes">Clientes y cobros</Link>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setExportDialogOpen(true)}>
            <Download className="h-4 w-4 mr-1.5" /> Exportar
          </Button>
          {canCreate && (
            <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
              <Sparkles className="h-4 w-4 mr-1.5" /> Importar Factura PDF
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={openNewDialog}>
              <Plus className="mr-1.5 h-4 w-4" /> Nueva Orden
            </Button>
          )}
        </PageHeader>

        {/* Date Filter (unificado con DateRangeFilter compartido) */}
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter
            preset={datePreset}
            customFrom={customFrom}
            customTo={customTo}
            onChange={setDateFilter}
          />
        </div>

        {/* Stats */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3">
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Total Órdenes</p>
            <p className="text-2xl font-display font-bold">{filteredOrders.length}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Pendientes</p>
            <p className="text-2xl font-display font-bold text-warning">
              {filteredOrders.filter((o) => o.status === "pending").length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Completadas</p>
            <p className="text-2xl font-display font-bold text-success">
              {filteredOrders.filter((o) => o.status === "delivered").length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Canceladas</p>
            <p className="text-2xl font-display font-bold text-destructive">
              {cancelledOrders}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Gastos Totales</p>
            <p className="text-2xl font-display font-bold text-primary">
              {totalExpenses.toLocaleString("es-ES")} €
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">Proveedores &gt; 3.000€</p>
            <p className="text-2xl font-display font-bold text-accent-foreground">
              {suppliersOver3k.length}
            </p>
            {suppliersOver3k.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {suppliersOver3k.slice(0, 3).map((s, i) => (
                  <p key={i} className="text-xs text-muted-foreground truncate">
                    {s.name}: {s.total.toLocaleString("es-ES")} €
                  </p>
                ))}
                {suppliersOver3k.length > 3 && (
                  <p className="text-xs text-muted-foreground">+{suppliersOver3k.length - 3} más</p>
                )}
              </div>
            )}
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">IVA (Compras)</p>
            <p className="text-2xl font-display font-bold text-primary">
              {totalIVA.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">RE Pagado</p>
            <p className="text-2xl font-display font-bold text-success">
              {totalREPagado.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-muted-foreground">RE Pendiente de Pago</p>
            <p className="text-2xl font-display font-bold text-warning">
              {totalREPendiente.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="chart-container">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por número o proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-secondary border-border"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-secondary border-border">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(statusConfig).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value.label}
                  </SelectItem>
                ))}
                <SelectItem value="pending_309">
                  <span className="flex items-center gap-1">
                    <Scale className="h-3 w-3" /> Pendiente Mod. 309
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedOrders.size > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5 flex-wrap">
            <span className="text-sm font-medium">{selectedOrders.size} seleccionado(s)</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">Cambiar estado</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {Object.entries(statusConfig).map(([k, v]) => (
                  <DropdownMenuItem key={k} onClick={() => handleBulkStatusChange(k)}>
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={() => { setBulkEditFields({}); setBulkEditOpen(true); }}>
              <FileText className="mr-1.5 h-3.5 w-3.5" /> Editar campos
            </Button>
            <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Eliminar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedOrders(new Set())}>Deseleccionar</Button>
          </div>
        )}

        {/* Table */}
        <div className="chart-container">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="data-table min-w-[900px]">
                <thead>
                  <tr>
                    <th className="w-[40px]">
                      <Checkbox
                        checked={sortedData.length > 0 && selectedOrders.size === sortedData.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <SortableTableHead field="order_number" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Orden</SortableTableHead>
                    <SortableTableHead field="supplier_invoice_number" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Nº Fact. Prov.</SortableTableHead>
                    <SortableTableHead field="supplier.name" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Proveedor</SortableTableHead>
                    <SortableTableHead field="warehouse" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Almacén</SortableTableHead>
                    <SortableTableHead field="type" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Tipo</SortableTableHead>
                    <SortableTableHead field="payment_method" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Pago</SortableTableHead>
                    <SortableTableHead field="tax_rate" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>IVA</SortableTableHead>
                    <SortableTableHead field="total" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Total</SortableTableHead>
                    <SortableTableHead field="created_at" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Fecha</SortableTableHead>
                    <SortableTableHead field="status" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Estado</SortableTableHead>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((order) => (
                    <tr key={order.id} className={cn(selectedOrders.has(order.id) && "bg-primary/5")}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedOrders.has(order.id)}
                          onCheckedChange={() => toggleSelect(order.id)}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          {order.supplier?.logo_url ? (
                            <img src={order.supplier.logo_url} alt={order.supplier.name} className="h-10 w-10 rounded-lg object-contain bg-muted border border-border" />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                              <ShoppingCart className="h-5 w-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-mono font-medium">{order.order_number}</p>
                            {order.invoice_file_path && (
                              <span className="text-xs text-success flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                Factura adjunta
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {(order as any).supplier_invoice_number || "-"}
                      </td>
                       <td>
                         <div>
                           <p className="font-medium max-w-[140px] break-words line-clamp-2 leading-tight">{order.supplier?.name || order.client?.name || "Sin proveedor"}</p>
                           {order.client?.company_name && (
                             <p className="text-sm text-muted-foreground">{order.client.company_name}</p>
                           )}
                         </div>
                        </td>
                      <td className="text-muted-foreground">
                        {order.warehouse?.name || "-"}
                      </td>
                      <td>
                        {orderTypes.find((t) => t.value === order.order_type)?.label || order.order_type}
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {paymentMethods.find((p) => p.value === order.payment_method)?.label || order.payment_method || "-"}
                      </td>
                      <td className="text-sm">
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className={cn(
                            (order as any).tax_type === 'iva_req' ? 'border-warning text-warning' : 'border-muted-foreground text-muted-foreground'
                          )}>
                            {(order as any).tax_type === 'iva_req' ? 'IVA+Req' : 'IVA'}
                          </Badge>
                          {(order as any).missing_equivalence_surcharge && (
                            <Badge variant="outline" className="border-amber-500 text-amber-500 text-[10px]">
                              RE 5,2% pend.
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="font-medium">
                        {(order.total ?? 0).toLocaleString("es-ES")} €
                      </td>
                      <td className="text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        <InlineDateEdit
                          value={order.order_date}
                          displayValue={new Date(order.order_date).toLocaleDateString("es-ES")}
                          onSave={async (newDate) => {
                            await supabase.from("orders").update({ order_date: newDate }).eq("id", order.id);
                            fetchOrders();
                            toast({ title: "Fecha actualizada" });
                          }}
                        />
                      </td>
                      <td>
                        <span className={cn("badge-status", statusConfig[order.status]?.class)}>
                          {statusConfig[order.status]?.label}
                        </span>
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover border-border">
                            <DropdownMenuItem onClick={() => openViewDialog(order)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalles
                            </DropdownMenuItem>
                            {order.invoice_file_path && (
                              <DropdownMenuItem onClick={() => handleDownloadInvoice(order)}>
                                <FileText className="h-4 w-4 mr-2" />
                                Descargar Factura
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <DropdownMenuItem onClick={() => openEditDialog(order)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(order.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {sortedData.length === 0 && (
                    <tr>
                      <td colSpan={12} className="text-center py-8 text-muted-foreground">
                        No se encontraron órdenes
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingOrder ? "Editar Orden" : "Nueva Orden"}
            </DialogTitle>
            <DialogDescription>
              {editingOrder ? "Modifica los datos de la orden" : "Crea una nueva orden"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Datos de la Orden</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor</Label>
                  <Select value={formData.supplier_id} onValueChange={(value) => {
                    const selectedSupplier = suppliers.find(s => s.id === value);
                    const supplierTaxType = (selectedSupplier as any)?.default_tax_type || 'iva';
                    setFormData({ ...formData, supplier_id: value, tax_type: supplierTaxType });
                    handleTaxTypeChange(supplierTaxType);
                  }}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border"><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} {s.contact_name && `(${s.contact_name})`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Orden</Label>
                  <Select value={formData.order_type} onValueChange={(value) => setFormData({ ...formData, order_type: value })}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {orderTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Almacén *</Label>
                  <Select value={formData.warehouse_id} onValueChange={(value) => setFormData({ ...formData, warehouse_id: value })}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border"><SelectValue placeholder="Seleccionar almacén" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {Object.entries(statusConfig).map(([key, config]) => <SelectItem key={key} value={key}>{config.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Modo de Pago</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {paymentMethods.map((pm) => <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Orden</Label>
                  <Input type="date" value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} className="h-9 text-sm bg-secondary border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Esperada</Label>
                  <Input type="date" value={formData.expected_date} onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })} className="h-9 text-sm bg-secondary border-border" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nº Factura Proveedor</Label>
                <Input placeholder="Ej: FP-2026-001" value={formData.supplier_invoice_number} onChange={(e) => setFormData({ ...formData, supplier_invoice_number: e.target.value })} className="h-9 text-sm bg-secondary border-border" />
              </div>
            </div>

            {/* Product Search */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Productos</h4>
                </div>
                <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      <Plus className="h-4 w-4 mr-1" />
                      Añadir Producto
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96 p-0 bg-popover border-border" align="end">
                    <Command className="bg-popover">
                      <CommandInput placeholder="Buscar por nombre, SKU o EAN..." className="border-0" />
                      <CommandList>
                        <CommandEmpty>No se encontraron productos</CommandEmpty>
                        <CommandGroup>
                          {products.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={`${product.name} ${product.sku} ${product.ean || ""}`}
                              onSelect={() => addProductToOrder(product)}
                              className="cursor-pointer"
                            >
                              <Package className="h-4 w-4 mr-2 text-muted-foreground" />
                              <div className="flex-1">
                                <p className="font-medium">{product.name}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>SKU: {product.sku}</span>
                                  {product.ean && (
                                    <>
                                      <span>•</span>
                                      <span>EAN: {product.ean}</span>
                                    </>
                                  )}
                                  <span>•</span>
                                  <span className={cn(
                                    "flex items-center gap-1",
                                    product.stock <= 0 && "text-destructive",
                                    product.stock > 0 && product.stock <= 5 && "text-warning"
                                  )}>
                                    {product.stock <= 0 && <AlertTriangle className="h-3 w-3" />}
                                    Stock: {product.stock}
                                  </span>
                                </div>
                              </div>
                              <span className="text-sm font-medium">
                                {(product.unit_price ?? 0).toLocaleString("es-ES")} €
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Order Items List */}
              {orderItems.length > 0 ? (
                <div className="border border-border rounded-lg divide-y divide-border">
                  {orderItems.map((item) => {
                    const product = products.find((p) => p.id === item.product_id);
                    const stock = product?.stock ?? 0;
                    const isOverStock = item.quantity > stock;
                    
                    return (
                      <div key={item.product_id} className="p-3 space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded bg-primary/20 flex items-center justify-center">
                            <Package className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.product_name}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{item.sku}</span>
                              <span>•</span>
                              <span className={cn(
                                stock <= 0 && "text-destructive",
                                stock > 0 && stock <= 5 && "text-warning"
                              )}>
                                Stock: {stock}
                              </span>
                              {isOverStock && (
                                <span className="text-destructive flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  Excede stock
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(item.product_id, parseInt(e.target.value) || 0)}
                              className={cn(
                                "w-16 h-8 text-center bg-secondary border-border",
                                isOverStock && "border-destructive"
                              )}
                            />
                            <span className="text-sm text-muted-foreground">×</span>
                            <span className="w-20 text-right text-sm">
                              {(item.unit_price ?? 0).toLocaleString("es-ES")} €
                            </span>
                            <div className="flex items-center gap-1 w-24">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={item.discount_percent}
                                onChange={(e) => updateItemDiscount(item.product_id, parseFloat(e.target.value) || 0)}
                                className="w-14 h-8 text-center bg-secondary border-border text-xs"
                                title="Descuento %"
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                            <span className="w-24 text-right font-medium">
                              {(item.total ?? 0).toLocaleString("es-ES")} €
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => removeItemFromOrder(item.product_id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="border border-dashed border-border rounded-lg p-6 text-center text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay productos añadidos</p>
                  <p className="text-xs">Usa el botón "Añadir Producto" para buscar y añadir productos</p>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="grid grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Subtotal</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.subtotal.toFixed(2)}
                  readOnly
                  className="bg-muted border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Dto. General %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.global_discount_percent}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    recalculateTotals(orderItems, formData.tax_type, val);
                  }}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de IVA</Label>
                <Select
                  value={formData.tax_type}
                  onValueChange={handleTaxTypeChange}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {taxTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Impuestos</Label>
                <Input
                  type="number"
                  value={formData.tax_amount.toFixed(2)}
                  readOnly
                  className="bg-muted border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <Input
                  type="number"
                  value={formData.total.toFixed(2)}
                  readOnly
                  className="bg-muted border-border font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="MXN">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {Object.entries(statusConfig).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>


            {/* Invoice Upload */}
            <div className="space-y-2">
              <Label>Factura del Proveedor (PDF/Imagen)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                  className="bg-secondary border-border"
                />
                {invoiceFile && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <span className="truncate max-w-[150px]">{invoiceFile.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setInvoiceFile(null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                {editingOrder?.invoice_file_path && !invoiceFile && (
                  <div className="flex items-center gap-2 text-sm text-success">
                    <FileText className="h-4 w-4" />
                    <span>Factura adjunta</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadInvoice(editingOrder)}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Descargar
                    </Button>
                  </div>
                )}
              </div>
              {uploadingInvoice && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Subiendo factura...
                </p>
              )}
            </div>

            {/* Missing Equivalence Surcharge block */}
            {formData.order_type === "purchase" && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-amber-500" />
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Recargo de equivalencia</h4>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="missing-re-form"
                    checked={formData.missing_equivalence_surcharge}
                    onCheckedChange={(checked) => {
                      const isChecked = !!checked;
                      const amount = isChecked ? Math.round(formData.subtotal * 0.052 * 100) / 100 : 0;
                      setFormData({
                        ...formData,
                        missing_equivalence_surcharge: isChecked,
                        missing_surcharge_rate: isChecked ? 5.2 : 0,
                        missing_surcharge_amount: amount,
                        include_in_model_309: isChecked,
                      });
                    }}
                  />
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="missing-re-form" className="text-sm font-medium cursor-pointer">
                      Falta pagar el Recargo de equivalencia del 5,2%
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Marca esta opción cuando el proveedor no haya incluido el recargo de equivalencia en la factura y deba autoliquidarse mediante el modelo 309.
                    </p>
                  </div>
                </div>
                {formData.missing_equivalence_surcharge && (
                  <div className="flex items-center gap-4 pl-7">
                    <div className="space-y-1">
                      <Label className="text-xs">Cuota recargo pendiente</Label>
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                        value={formData.missing_surcharge_amount.toFixed(2)}
                        readOnly
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tasa</Label>
                      <Input className="h-8 text-sm bg-muted border-border w-20" value="5,2%" readOnly />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* IVA 309 Expense - Inversión sujeto pasivo / Adquisición intracomunitaria */}
            {formData.order_type === "purchase" && (
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-blue-500" />
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">IVA Modelo 309 — Adq. intracomunitaria / Inversión sujeto pasivo</h4>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="iva-309-form"
                    checked={formData.iva_309_expense}
                    onCheckedChange={(checked) => {
                      const isChecked = !!checked;
                      if (!isChecked) {
                        setFormData({
                          ...formData,
                          iva_309_expense: false,
                          iva_309_mode: "",
                          iva_309_expense_rate: 0,
                          iva_309_expense_amount: 0,
                          iva_309_re_amount: 0,
                          include_in_model_309: formData.missing_equivalence_surcharge,
                        });
                      } else {
                        const ivaAmount = Math.round(formData.subtotal * 0.21 * 100) / 100;
                        const reAmount = Math.round(formData.subtotal * 0.052 * 100) / 100;
                        setFormData({
                          ...formData,
                          iva_309_expense: true,
                          iva_309_mode: "iva_re",
                          iva_309_expense_rate: 21,
                          iva_309_expense_amount: ivaAmount,
                          iva_309_re_amount: reAmount,
                          include_in_model_309: true,
                        });
                      }
                    }}
                  />
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="iva-309-form" className="text-sm font-medium cursor-pointer">
                      Autorrepercusión Modelo 309
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Para bienes adquiridos en operaciones intracomunitarias o con inversión del sujeto pasivo.
                    </p>
                  </div>
                </div>
                {formData.iva_309_expense && (
                  <div className="space-y-3 pl-7">
                    <RadioGroup
                      value={formData.iva_309_mode}
                      onValueChange={(value: "iva_re" | "solo_iva") => {
                        const ivaAmount = Math.round(formData.subtotal * 0.21 * 100) / 100;
                        const reAmount = value === "iva_re" ? Math.round(formData.subtotal * 0.052 * 100) / 100 : 0;
                        setFormData({
                          ...formData,
                          iva_309_mode: value,
                          iva_309_expense_rate: 21,
                          iva_309_expense_amount: ivaAmount,
                          iva_309_re_amount: reAmount,
                        });
                      }}
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="iva_re" id="mode-iva-re" />
                        <Label htmlFor="mode-iva-re" className="text-sm cursor-pointer">Autorrepercutir IVA (21%) + RE (5,2%)</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="solo_iva" id="mode-solo-iva" />
                        <Label htmlFor="mode-solo-iva" className="text-sm cursor-pointer">Autorrepercutir solo IVA (21%)</Label>
                      </div>
                    </RadioGroup>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="space-y-1">
                        <Label className="text-xs">IVA autorrepercutido</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                          value={formData.iva_309_expense_amount.toFixed(2)}
                          onChange={(e) => setFormData({ ...formData, iva_309_expense_amount: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tasa IVA</Label>
                        <Input className="h-8 text-sm bg-muted border-border w-20" value="21%" readOnly />
                      </div>
                      {formData.iva_309_mode === "iva_re" && (
                        <>
                          <div className="space-y-1">
                            <Label className="text-xs">RE autorrepercutido</Label>
                            <Input
                              type="number"
                              step="0.01"
                              className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                              value={formData.iva_309_re_amount.toFixed(2)}
                              onChange={(e) => setFormData({ ...formData, iva_309_re_amount: parseFloat(e.target.value) || 0 })}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Tasa RE</Label>
                            <Input className="h-8 text-sm bg-muted border-border w-20" value="5,2%" readOnly />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-secondary border-border"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingOrder ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">Exportar Órdenes</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Period mode tabs */}
            <div className="flex gap-2">
              {(["monthly", "quarterly", "yearly"] as const).map((mode) => (
                <Button
                  key={mode}
                  variant={exportMode === mode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportMode(mode)}
                >
                  {mode === "monthly" ? "Mensual" : mode === "quarterly" ? "Trimestral" : "Anual"}
                </Button>
              ))}
            </div>

            {/* Year + Month/Quarter selectors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm">Año</Label>
                <Select value={String(exportYear)} onValueChange={(v) => setExportYear(Number(v))}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {exportMode === "monthly" && (
                <div className="space-y-1">
                  <Label className="text-sm">Mes</Label>
                  <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(Number(v))}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {monthNames.map((m, i) => (
                        <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {exportMode === "quarterly" && (
                <div className="space-y-1">
                  <Label className="text-sm">Trimestre</Label>
                  <Select value={String(exportQuarter)} onValueChange={(v) => setExportQuarter(Number(v))}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="1">T1 (Ene-Mar)</SelectItem>
                      <SelectItem value="2">T2 (Abr-Jun)</SelectItem>
                      <SelectItem value="3">T3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">T4 (Oct-Dic)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Status filter */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm">Estado</Label>
                <Select value={exportStatus} onValueChange={setExportStatus}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Tipo de IVA</Label>
                <Select value={exportTaxType} onValueChange={setExportTaxType}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="iva">Solo IVA</SelectItem>
                    <SelectItem value="iva_req">Solo IVA+Req</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Órdenes encontradas: {exportFilteredOrders.length} — con archivo adjunto: {exportFilteredOrders.filter(o => o.invoice_file_path).length}
            </p>

            {downloadingZip && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Descargando... {downloadProgress}%</p>
                <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: `${downloadProgress}%` }} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancelar
            </Button>
            <ExportRangeSummary
              presetLabel={datePresets.find(p => p.value === datePreset)?.label || datePreset || "Todos"}
              range={activeRange}
              triggerLabel="Exportar con resumen"
              formats={[
                { key: "excel", label: "Excel", icon: "excel", disabled: downloadingZip },
                { key: "zip", label: "PDFs (ZIP)", icon: "zip", disabled: downloadingZip },
                { key: "json", label: "JSON", icon: "json", disabled: downloadingZip },
              ]}
              onExport={(fmt) => {
                if (fmt === "excel") return handleExportExcel();
                if (fmt === "zip") return handleExportZip();
                if (fmt === "json") {
                  const meta = {
                    rango_desde_iso: activeRange?.from.toISOString() ?? null,
                    rango_hasta_iso: activeRange?.to.toISOString() ?? null,
                    zona_horaria: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    offset_min: -new Date().getTimezoneOffset(),
                  };
                  const blob = new Blob([JSON.stringify({ meta, items: filteredOrders }, null, 2)], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `pedidos_${meta.rango_desde_iso ?? "all"}.json`; a.click();
                  URL.revokeObjectURL(url);
                }
              }}
            />
            <Button variant="outline" onClick={handleExportZip} disabled={downloadingZip}>
              <FileText className="h-4 w-4 mr-2" />
              Exportar PDFs (ZIP)
            </Button>
            <Button onClick={handleExportExcel} disabled={downloadingZip}>
              <Download className="h-4 w-4 mr-2" />
              Exportar Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Import Dialog */}
      <InvoiceImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        suppliers={suppliers}
        onOrderCreated={() => {
          fetchOrders();
          fetchProducts();
        }}
      />

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Detalles de Orden: {viewOrder?.order_number}</DialogTitle>
            <DialogDescription>
              {viewOrder?.order_date ? new Date(viewOrder.order_date).toLocaleDateString("es-ES") : ""}
            </DialogDescription>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Proveedor</p>
                  <p className="font-medium">{viewOrder.supplier?.name || viewOrder.client?.name || "Sin proveedor"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Almacén</p>
                  <p className="font-medium">{viewOrder.warehouse?.name || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Tipo</p>
                  <p className="font-medium">{orderTypes.find(t => t.value === viewOrder.order_type)?.label || viewOrder.order_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estado</p>
                  <span className={cn("badge-status", statusConfig[viewOrder.status]?.class)}>
                    {statusConfig[viewOrder.status]?.label}
                  </span>
                </div>
                <div>
                  <p className="text-muted-foreground">Modo de Pago</p>
                  <p className="font-medium">{paymentMethods.find(p => p.value === viewOrder.payment_method)?.label || viewOrder.payment_method || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Nº Factura Proveedor</p>
                  <p className="font-medium">{(viewOrder as any).supplier_invoice_number || "-"}</p>
                </div>
              </div>

              {/* Products */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Productos</h4>
                {viewOrderItems.length > 0 ? (
                  <div className="border border-border rounded-lg divide-y divide-border">
                    {viewOrderItems.map((item) => (
                      <div key={item.product_id} className="flex items-center justify-between p-3 text-sm">
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-muted-foreground">{item.sku}</p>
                        </div>
                        <div className="text-right">
                          <p>{item.quantity} x {item.unit_price.toLocaleString("es-ES")} €</p>
                          <p className="font-medium">{item.total.toLocaleString("es-ES")} €</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin productos</p>
                )}
              </div>

              {/* Totals */}
              <div className="bg-secondary rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{(viewOrder.subtotal ?? 0).toLocaleString("es-ES")} €</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Impuestos</span><span>{(viewOrder.tax_amount ?? 0).toLocaleString("es-ES")} €</span></div>
                <div className="flex justify-between font-bold text-base"><span>Total</span><span>{(viewOrder.total ?? 0).toLocaleString("es-ES")} €</span></div>
              </div>

              {/* Missing RE Surcharge info */}
              {(viewOrder as any).missing_equivalence_surcharge && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium">Recargo de equivalencia pendiente (Mod. 309)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Base afectada</p>
                      <p className="font-medium">{(viewOrder.subtotal ?? 0).toLocaleString("es-ES")} €</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Tasa</p>
                      <p className="font-medium">{(viewOrder as any).missing_surcharge_rate || 5.2}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Recargo pendiente</p>
                      <p className="font-semibold text-amber-600">{((viewOrder as any).missing_surcharge_amount || 0).toLocaleString("es-ES")} €</p>
                    </div>
                  </div>
                </div>
              )}

              {viewOrder.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notas</p>
                  <p className="text-sm bg-secondary rounded p-2">{viewOrder.notes}</p>
                </div>
              )}

              {/* Invoice file */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Archivo Adjunto</h4>
                {viewOrder.invoice_file_path ? (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-success" />
                    <span className="text-sm text-success">Factura adjunta</span>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadInvoice(viewOrder)}>
                      <Download className="h-3 w-3 mr-1" /> Descargar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Sin archivo adjunto</p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="bg-secondary border-border max-w-xs"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleAttachFile(viewOrder, file);
                        }}
                        disabled={attachingFile}
                      />
                      {attachingFile && <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar {selectedOrders.size} órdenes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Solo se actualizarán los campos que rellenes. Los campos vacíos no se modificarán.</p>
          <div className="space-y-4">
            <div>
              <Label>Estado</Label>
              <Select value={bulkEditFields.status || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(statusConfig).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proveedor</Label>
              <Select value={bulkEditFields.supplier_id || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, supplier_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Almacén</Label>
              <Select value={bulkEditFields.warehouse_id || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, warehouse_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Orden</Label>
              <Select value={bulkEditFields.order_type || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, order_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {orderTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Modo de Pago</Label>
              <Select value={bulkEditFields.payment_method || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, payment_method: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de IVA</Label>
              <Select value={bulkEditFields.tax_type || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, tax_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Sin cambio" /></SelectTrigger>
                <SelectContent>
                  {taxTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={bulkEditFields.missing_equivalence_surcharge || false}
                onCheckedChange={v => setBulkEditFields(p => ({ ...p, missing_equivalence_surcharge: !!v }))}
              />
              <Label>Falta pagar RE 5,2%</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={bulkEditFields.iva_309_expense || false}
                onCheckedChange={v => setBulkEditFields(p => ({ ...p, iva_309_expense: !!v, ...(v ? {} : { iva_309_mode: undefined }) }))}
              />
              <Label>IVA Modelo 309</Label>
            </div>
            {bulkEditFields.iva_309_expense && (
              <div>
                <Label>Modo 309</Label>
                <Select value={bulkEditFields.iva_309_mode || ""} onValueChange={v => setBulkEditFields(p => ({ ...p, iva_309_mode: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iva_re">IVA + RE</SelectItem>
                    <SelectItem value="solo_iva">Solo IVA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notas</Label>
              <Textarea placeholder="Añadir notas a todos los seleccionados..." value={bulkEditFields.notes || ""} onChange={e => setBulkEditFields(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleBulkEdit}
              disabled={Object.keys(bulkEditFields).filter(k => bulkEditFields[k] !== undefined && bulkEditFields[k] !== "").length === 0}
            >
              Aplicar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
